"use strict";
/**
 * DAG (Directed Acyclic Graph) Module for Flag Dependencies
 *
 * This module handles the dependency resolution between flags.
 * Flags can depend on other flags, and we need to evaluate them in the correct order.
 *
 * IMPORTANT: Dependencies are now defined in the DATA layer (FlagProgram.inputs.flags),
 * not in the CODE layer (FlagDefinition). This module works with FlagProgram inputs.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MissingDependencyError = exports.CyclicDependencyError = void 0;
exports.buildDependencyGraphFromPrograms = buildDependencyGraphFromPrograms;
exports.buildDependencyGraph = buildDependencyGraph;
exports.validateProgramDependencies = validateProgramDependencies;
exports.validateDependencies = validateDependencies;
exports.detectCycle = detectCycle;
exports.topologicalSortPrograms = topologicalSortPrograms;
exports.topologicalSort = topologicalSort;
exports.getDependentsFromPrograms = getDependentsFromPrograms;
exports.getDependents = getDependents;
exports.getAllDependenciesFromPrograms = getAllDependenciesFromPrograms;
exports.getAllDependencies = getAllDependencies;
exports.validateRegistryAndPrograms = validateRegistryAndPrograms;
exports.validateRegistry = validateRegistry;
/**
 * Error thrown when a cycle is detected in the flag dependency graph
 */
class CyclicDependencyError extends Error {
    cycle;
    constructor(cycle, message) {
        super(message ?? `Cyclic dependency detected: ${cycle.join(" -> ")}`);
        this.cycle = cycle;
        this.name = "CyclicDependencyError";
    }
}
exports.CyclicDependencyError = CyclicDependencyError;
/**
 * Error thrown when a flag depends on a non-existent flag
 */
class MissingDependencyError extends Error {
    flagKey;
    missingDependency;
    constructor(flagKey, missingDependency) {
        super(`Flag "${flagKey}" depends on non-existent flag "${missingDependency}"`);
        this.flagKey = flagKey;
        this.missingDependency = missingDependency;
        this.name = "MissingDependencyError";
    }
}
exports.MissingDependencyError = MissingDependencyError;
/**
 * Build a dependency graph from flag programs (DATA layer)
 * Returns a map of flag key to its dependencies (from FlagProgram.inputs.flags)
 */
function buildDependencyGraphFromPrograms(programs) {
    const graph = new Map();
    for (const [key, program] of Object.entries(programs)) {
        graph.set(key, program.inputs.flags ?? []);
    }
    return graph;
}
/**
 * Build a dependency graph from flag definitions (CODE layer)
 * This is a fallback for when no programs are available - returns empty dependencies
 * @deprecated Use buildDependencyGraphFromPrograms instead
 */
function buildDependencyGraph(registry) {
    const graph = new Map();
    for (const key of Object.keys(registry)) {
        // FlagDefinition no longer has dependencies - they're in FlagProgram
        graph.set(key, []);
    }
    return graph;
}
/**
 * Validate that all dependencies exist in the registry (using FlagProgram inputs)
 */
function validateProgramDependencies(programs, registry) {
    const errors = [];
    const flagKeys = new Set(Object.keys(registry));
    for (const [key, program] of Object.entries(programs)) {
        for (const dep of program.inputs.flags ?? []) {
            if (!flagKeys.has(dep)) {
                errors.push(new MissingDependencyError(key, dep));
            }
        }
    }
    return errors;
}
/**
 * @deprecated Use validateProgramDependencies instead
 */
function validateDependencies(registry) {
    // FlagDefinition no longer has dependencies - they're in FlagProgram
    // This function is kept for backwards compatibility but returns no errors
    void registry;
    return [];
}
/**
 * Detect cycles in the dependency graph using DFS
 * Returns the first cycle found, or undefined if no cycles exist
 */
function detectCycle(graph) {
    const visited = new Set();
    const recursionStack = new Set();
    const path = [];
    function dfs(node) {
        visited.add(node);
        recursionStack.add(node);
        path.push(node);
        const dependencies = graph.get(node) ?? [];
        for (const dep of dependencies) {
            if (!visited.has(dep)) {
                const cycle = dfs(dep);
                if (cycle) {
                    return cycle;
                }
            }
            else if (recursionStack.has(dep)) {
                // Found a cycle - extract it from the path
                const cycleStart = path.indexOf(dep);
                return [...path.slice(cycleStart), dep];
            }
        }
        path.pop();
        recursionStack.delete(node);
        return undefined;
    }
    for (const node of graph.keys()) {
        if (!visited.has(node)) {
            const cycle = dfs(node);
            if (cycle) {
                return cycle;
            }
        }
    }
    return undefined;
}
/**
 * Topologically sort the flags based on their dependencies from FlagProgram inputs
 * Flags with no dependencies come first, then flags that depend on them, etc.
 *
 * @throws CyclicDependencyError if a cycle is detected
 */
function topologicalSortPrograms(programs, registry) {
    const graph = buildDependencyGraphFromPrograms(programs);
    // Add any flags from registry that don't have programs (they have no dependencies)
    for (const key of Object.keys(registry)) {
        if (!graph.has(key)) {
            graph.set(key, []);
        }
    }
    // Check for cycles first
    const cycle = detectCycle(graph);
    if (cycle) {
        throw new CyclicDependencyError(cycle);
    }
    const sorted = [];
    const visited = new Set();
    const visiting = new Set();
    function visit(node) {
        if (visited.has(node)) {
            return;
        }
        if (visiting.has(node)) {
            // This shouldn't happen since we checked for cycles above
            throw new CyclicDependencyError([node]);
        }
        visiting.add(node);
        const dependencies = graph.get(node) ?? [];
        for (const dep of dependencies) {
            visit(dep);
        }
        visiting.delete(node);
        visited.add(node);
        sorted.push(node);
    }
    for (const node of graph.keys()) {
        visit(node);
    }
    return sorted;
}
/**
 * @deprecated Use topologicalSortPrograms instead
 */
function topologicalSort(registry) {
    // FlagDefinition no longer has dependencies - they're in FlagProgram
    // This function returns flags in arbitrary order (no dependencies)
    return Object.keys(registry);
}
/**
 * Get all flags that depend on a given flag (direct and transitive)
 * Useful for determining which flags need re-evaluation when a flag changes
 */
function getDependentsFromPrograms(programs, flagKey) {
    const dependents = new Set();
    const visited = new Set();
    function findDependents(key) {
        for (const [otherKey, program] of Object.entries(programs)) {
            if (visited.has(otherKey)) {
                continue;
            }
            const deps = program.inputs.flags ?? [];
            if (deps.includes(key)) {
                dependents.add(otherKey);
                visited.add(otherKey);
                findDependents(otherKey);
            }
        }
    }
    findDependents(flagKey);
    return Array.from(dependents);
}
/**
 * @deprecated Use getDependentsFromPrograms instead
 */
function getDependents(registry, flagKey) {
    // FlagDefinition no longer has dependencies - they're in FlagProgram
    void registry;
    void flagKey;
    return [];
}
/**
 * Get all dependencies of a flag (direct and transitive) from FlagProgram inputs
 */
function getAllDependenciesFromPrograms(programs, flagKey) {
    const dependencies = new Set();
    const visited = new Set();
    function collectDependencies(key) {
        if (visited.has(key)) {
            return;
        }
        visited.add(key);
        const program = programs[key];
        if (!program) {
            return;
        }
        for (const dep of program.inputs.flags ?? []) {
            dependencies.add(dep);
            collectDependencies(dep);
        }
    }
    collectDependencies(flagKey);
    return Array.from(dependencies);
}
/**
 * @deprecated Use getAllDependenciesFromPrograms instead
 */
function getAllDependencies(registry, flagKey) {
    // FlagDefinition no longer has dependencies - they're in FlagProgram
    void registry;
    void flagKey;
    return [];
}
/**
 * Validate the registry and programs for dependency issues
 */
function validateRegistryAndPrograms(registry, programs) {
    const missingDependencies = validateProgramDependencies(programs, registry);
    const graph = buildDependencyGraphFromPrograms(programs);
    // Add any flags from registry that don't have programs
    for (const key of Object.keys(registry)) {
        if (!graph.has(key)) {
            graph.set(key, []);
        }
    }
    const cycle = detectCycle(graph);
    return {
        valid: missingDependencies.length === 0 && !cycle,
        missingDependencies,
        cycle,
    };
}
/**
 * @deprecated Use validateRegistryAndPrograms instead
 */
function validateRegistry(registry) {
    // FlagDefinition no longer has dependencies - they're in FlagProgram
    // This function returns valid with no issues for backwards compatibility
    void registry;
    return {
        valid: true,
        missingDependencies: [],
        cycle: undefined,
    };
}
//# sourceMappingURL=dag.js.map